// ---------------------------------------------------------------------------
// Geometria e memória de uma janela que vive DENTRO da área do 3D.
//
// Nasceu no painel dos pins BCF (`viewer/bcf/lib/janelaDosPins.ts`) e veio
// para aqui quando o Mapa precisou do mesmo (Rui, 26/10/2026: "só consigo
// fazer drag de um lado para o outro, não dá para redimensionar e não dá
// para pôr noutro ecrã"). A janela arrasta-se pela barra do título, nunca
// sai da área, muda de tamanho pela pega do canto e minimiza-se para uma
// barra. O componente é `JanelaNo3D.tsx`.
//
// Posição, tamanho e minimizado ficam por browser (localStorage), sempre
// com try/catch: num modo privado ou com o armazenamento bloqueado, a
// janela abre no sítio e com o tamanho por omissão e o resto funciona. É
// preferência, não trabalho: as chaves não entram na recuperação da sessão.
//
// Puro (menos as duas funções de armazenamento, que recebem o Storage).
// ---------------------------------------------------------------------------

import { limitarPosicao, type Ponto, type Tamanho } from '@viewer/section-box/janelaSectionBox'

export { limitarPosicao }
export type { Ponto, Tamanho }

export interface EstadoDaJanela {
  /** Posição relativa ao contentor do 3D; null = por omissão. */
  pos: Ponto | null
  /** Tamanho escolhido pelo utilizador; null = por omissão. */
  tamanho: Tamanho | null
  minimizado: boolean
}

/** Onde a janela abre da primeira vez. */
export type Canto = 'inferior-esquerdo' | 'inferior-direito'

/** Margem aos cantos da área. */
export const MARGEM = 12

/** O tamanho dentro dos limites: nunca abaixo do mínimo, nunca maior do
 *  que a área (menos as margens). Numa área mais pequena do que o mínimo,
 *  fica o mínimo (a área decide, a janela encosta-se ao canto). */
export function limitarTamanho(t: Tamanho, area: Tamanho, minimo: Tamanho): Tamanho {
  const maxW = Math.max(minimo.w, area.w - 2 * MARGEM)
  const maxH = Math.max(minimo.h, area.h - 2 * MARGEM)
  return {
    w: Math.round(Math.min(Math.max(minimo.w, t.w), maxW)),
    h: Math.round(Math.min(Math.max(minimo.h, t.h), maxH)),
  }
}

/** Ao arrastar a pega: o tamanho novo não deixa a janela passar da borda
 *  direita nem da de baixo da área. */
export function redimensionar(t0: Tamanho, delta: Ponto, pos: Ponto, area: Tamanho, minimo: Tamanho): Tamanho {
  const alvo = { w: t0.w + delta.x, h: t0.h + delta.y }
  const dentro = {
    w: Math.min(alvo.w, Math.max(minimo.w, area.w - pos.x)),
    h: Math.min(alvo.h, Math.max(minimo.h, area.h - pos.y)),
  }
  return limitarTamanho(dentro, area, minimo)
}

/** Posição por omissão: no canto pedido, dentro da área. */
export function posicaoPorOmissao(area: Tamanho, janela: Tamanho, canto: Canto = 'inferior-esquerdo'): Ponto {
  const x = canto === 'inferior-direito' ? area.w - janela.w - MARGEM : MARGEM
  return limitarPosicao({ x, y: area.h - janela.h - MARGEM }, janela, area)
}

function numero(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v)
}

function armazenamento(storage?: Storage | null): Storage | null {
  return storage ?? (typeof localStorage === 'undefined' ? null : localStorage)
}

/** Lê o estado guardado. Qualquer falha dá o estado por omissão. */
export function lerJanela(chave: string, storage?: Storage | null): EstadoDaJanela {
  const vazio: EstadoDaJanela = { pos: null, tamanho: null, minimizado: false }
  try {
    const raw = armazenamento(storage)?.getItem(chave)
    if (!raw) return vazio
    const o = JSON.parse(raw) as Partial<{
      pos: Partial<Ponto> | null
      tamanho: Partial<Tamanho> | null
      minimizado: unknown
    }>
    const pos = o.pos && numero(o.pos.x) && numero(o.pos.y) ? { x: o.pos.x, y: o.pos.y } : null
    const tamanho =
      o.tamanho && numero(o.tamanho.w) && numero(o.tamanho.h) && o.tamanho.w > 0 && o.tamanho.h > 0
        ? { w: o.tamanho.w, h: o.tamanho.h }
        : null
    return { pos, tamanho, minimizado: o.minimizado === true }
  } catch {
    return vazio
  }
}

/** Grava o estado. Falhar a gravar não é erro: perde-se só a memória. */
export function gravarJanela(chave: string, estado: EstadoDaJanela, storage?: Storage | null): void {
  try {
    armazenamento(storage)?.setItem(chave, JSON.stringify(estado))
  } catch {
    /* armazenamento cheio ou bloqueado */
  }
}
